import { gateOpensWords, type BuilderWho } from '@nsb/core';
import { useId, useState, type FormEvent } from 'react';
import { Button, Seg, TextInput } from '../ui.tsx';
import type { TilePhase, TileStep } from './model.ts';
import { OWN_ASKS, OWN_KIND_WORDS, OWN_WHO_WORDS, afterChoices, ownStepBody, type OwnKind, type OwnStepBody } from './ownStep.ts';
import { BandMark } from './Tile.tsx';

/**
 * A step the tree does not have, for one job (design study 12, own step A, as Jason answered it, 2026-10-06: "the
 * master's tools at the top of the job's page, as drawn. Drag Work, Decision, A gate or Milestone into a phase; it lands
 * on that phase's first row with its card open (Title, Who does it, Comes after), the job's alone"). Its card opens as
 * the kind is dropped: its title, who does it (who decides a decision, who approves a gate, as the master's card asks:
 * nobody, and it happens), and what it comes after, all set before it is added, as one change in the job's draft. More
 * lines are drawn from its dot once it is on the job.
 */

export function OwnStepCard({
  kind,
  phase,
  phases,
  steps,
  busy,
  onAdd,
  onCancel,
}: {
  kind: OwnKind;
  phase: string | null;
  phases: readonly TilePhase[];
  steps: readonly TileStep[];
  busy?: boolean;
  onAdd: (body: OwnStepBody) => void;
  onCancel: () => void;
}) {
  const asks = OWN_ASKS[kind];
  const id = useId();
  const [title, setTitle] = useState('');
  const [who, setWho] = useState<BuilderWho | null>(asks?.first ?? null);
  const [after, setAfter] = useState('');
  const groups = afterChoices(phases, steps);
  const phaseName = phases.find((p) => p.key === phase)?.name ?? null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (title.trim()) onAdd(ownStepBody(kind, phase, { title, who, after: after || null }));
  };
  return (
    <form className="p-5" onSubmit={submit} aria-label="This job’s own step">
      <p className="label flex flex-wrap items-center gap-1.5">
        <span>This job’s own step</span>
        {kind === 'gate' ? (
          <span className="inline-flex items-center gap-1.5" role="img" aria-label="Holds back what comes after it">
            · <BandMark mark="gate" size={12} />
          </span>
        ) : (
          <span>· {OWN_KIND_WORDS[kind]}</span>
        )}
        {phaseName ? <span>· {phaseName}</span> : null}
      </p>
      <label className="label mt-4 block" htmlFor={`${id}-title`}>
        Title
      </label>
      <TextInput id={`${id}-title`} placeholder="What the step is" value={title} autoFocus onChange={(e) => setTitle(e.target.value)} className="field mt-1 w-full max-w-md" />
      {asks ? (
        <section className="mt-4">
          <h3 className="label mb-1.5">{asks.title}</h3>
          <Seg<BuilderWho> label={asks.title} value={who} options={asks.options} words={(w) => OWN_WHO_WORDS[w]} onPick={setWho} keep={kind !== 'gate'} />
          {kind === 'gate' ? (
            <p className="meta mt-1.5">
              {who === 'client'
                ? 'The client approves it on their home once the work reaches it; their approval is on the record.'
                : who
                  ? `${gateOpensWords(who)} it; NSB marks it done when they have.`
                  : 'Nobody approves it: it happens. Choose who approves it, if someone does.'}
            </p>
          ) : null}
        </section>
      ) : (
        <p className="meta mt-4">Nobody does a milestone: it is reached when all it needs is done.</p>
      )}
      <label className="label mt-4 block" htmlFor={`${id}-after`}>
        Comes after
      </label>
      <select id={`${id}-after`} className="field mt-1 w-full max-w-md" value={after} onChange={(e) => setAfter(e.target.value)}>
        <option value="">Nothing yet</option>
        {groups.map((g) => (
          <optgroup key={g.name} label={g.name}>
            {g.steps.map((s) => (
              <option key={s.key} value={s.key}>
                {s.title}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <p className="meta mt-1">Choose one here, or draw a line from a step’s dot once it is on the job.</p>
      <div className="mt-5 flex flex-wrap items-center gap-4">
        <Button type="submit" busy={busy} disabled={!title.trim()}>
          Add it to the job
        </Button>
        <Button tone="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
